/**
 * A dependency-bearing item that can be topologically sorted.
 */
export interface TopologicalSortItem {
  id: string;
  dependencies?: { id: string }[];
}

/**
 * Topologically sorts items so that dependencies come before dependents.
 * Items with no dependencies (or dependencies not in the set) come first.
 *
 * Uses Kahn's algorithm for topological sorting.
 *
 * If a cycle is detected (some items cannot be processed), the remaining
 * items are appended at the end so no items are silently dropped.
 */
export const topologicalSortEntities = <T extends TopologicalSortItem>(
  entries: T[],
): T[] => {
  if (entries.length === 0) return [];

  const entryMap = new Map(entries.map((e) => [e.id, e]));

  // Initialize in-degree for all entries to 0
  const inDegree = new Map<string, number>(entries.map((e) => [e.id, 0]));

  // Build reverse adjacency map: for each dependency, which entries depend on it.
  // This avoids scanning all entries on every dequeue (O(V+E) instead of O(V²)).
  const dependentsOf = new Map<string, string[]>();

  entries.forEach((entry) => {
    const deps = entry.dependencies ?? [];
    const depsInSet = deps.filter((dep) => entryMap.has(dep.id));
    inDegree.set(entry.id, depsInSet.length);
    depsInSet.forEach((dep) => {
      const list = dependentsOf.get(dep.id) ?? [];
      list.push(entry.id);
      dependentsOf.set(dep.id, list);
    });
  });

  // Start with entries that have no dependencies (in-degree = 0)
  const queue: T[] = entries.filter((e) => inDegree.get(e.id) === 0);

  const result: T[] = [];

  while (queue.length > 0) {
    const current = queue.shift()!;
    result.push(current);

    // Decrement in-degree for all entries that depend on current
    const dependents = dependentsOf.get(current.id) ?? [];
    dependents.forEach((dependentId) => {
      const newDegree = (inDegree.get(dependentId) ?? 1) - 1;
      inDegree.set(dependentId, newDegree);
      if (newDegree === 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- dependentId came from entries, so entryMap has it
        queue.push(entryMap.get(dependentId)!);
      }
    });
  }

  // If we couldn't process all entries, there's a cycle - append remaining
  // (this shouldn't happen with valid data, but handles it gracefully)
  if (result.length < entries.length) {
    const processed = new Set(result.map((e) => e.id));
    const remaining = entries.filter((entry) => !processed.has(entry.id));
    return [...result, ...remaining];
  }

  return result;
};
